import { apiClient, ProductResponse, PaginatedResponse, ProductWithPricing, CustomerCatalogResponse, GroupedCatalogResponse, getAuthHeaders } from './api';

export interface ProductSearchParams {
  limit?: number;
  offset?: number;
  category?: string;
  search?: string;
  q?: string; // for search endpoint
  family_code?: string;
  subfamily_code?: string;
  brand_code?: string;
  category_item_code?: string;
}

export interface ProductSearchResponse {
  items: ProductResponse[];
  total: number;
  query?: string;
}

class ProductService {
  /**
   * Get customer-specific product catalog with pricing
   */
  async getCustomerCatalog(params: ProductSearchParams = {}): Promise<CustomerCatalogResponse> {
    try {
      const headers = getAuthHeaders();
      const queryParams = new URLSearchParams();
      
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.search) queryParams.append('search', params.search);
      if (params.family_code) queryParams.append('family_code', params.family_code);
      if (params.subfamily_code) queryParams.append('subfamily_code', params.subfamily_code);
      if (params.brand_code) queryParams.append('brand_code', params.brand_code);
      if (params.category_item_code) queryParams.append('category_item_code', params.category_item_code);

      const endpoint = `/products/catalog?${queryParams.toString()}`;
      const response = await apiClient.get<CustomerCatalogResponse>(endpoint, headers);
      
      return response;
    } catch (error) {
      console.error('Get customer catalog error:', error);
      throw error;
    }
  }

  /**
   * Get grouped product catalog (color variants grouped together)
   */
  async getGroupedCatalog(params: ProductSearchParams = {}): Promise<GroupedCatalogResponse> {
    try {
      const headers = getAuthHeaders();
      const queryParams = new URLSearchParams();

      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.search) queryParams.append('search', params.search);
      if (params.family_code) queryParams.append('family_code', params.family_code);
      if (params.subfamily_code) queryParams.append('subfamily_code', params.subfamily_code);
      if (params.brand_code) queryParams.append('brand_code', params.brand_code);
      if (params.category_item_code) queryParams.append('category_item_code', params.category_item_code);

      const endpoint = `/products/catalog/grouped?${queryParams.toString()}`;
      const response = await apiClient.get<GroupedCatalogResponse>(endpoint, headers);
      return response;
    } catch (error) {
      console.error('Get grouped catalog error:', error);
      throw error;
    }
  }

  /**
   * Typeahead suggestions for the catalog search box. Backed by the
   * MySQL FULLTEXT index on products.search_text.
   */
  async getCatalogSuggestions(q: string, limit = 8): Promise<{
    variants: Array<{
      id: string;
      name: string;
      part_number: string;
      image_url: string | null;
      group_key: string;
      group_name: string | null;
      category_label: string | null;
    }>;
    groups: Array<{
      group_key: string;
      group_name: string;
      category_label: string | null;
    }>;
  }> {
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams({ q, limit: String(limit) });
      const endpoint = `/products/catalog/suggestions?${params.toString()}`;
      return await apiClient.get(endpoint, headers);
    } catch (error) {
      console.error('Get catalog suggestions error:', error);
      return { variants: [], groups: [] };
    }
  }

  /**
   * Get variants for a grouped product by group key
   */
  async getGroupVariants(groupKey: string): Promise<any> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.get(
        `/products/catalog/grouped/${encodeURIComponent(groupKey)}/variants`,
        headers,
      );
      return response;
    } catch (error) {
      console.error('Get group variants error:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of products
   */
  async getProducts(params: ProductSearchParams = {}): Promise<PaginatedResponse<ProductResponse>> {
    try {
      const headers = getAuthHeaders();
      const queryParams = new URLSearchParams();
      
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.search) queryParams.append('search', params.search);
      if (params.family_code) queryParams.append('family_code', params.family_code);
      if (params.subfamily_code) queryParams.append('subfamily_code', params.subfamily_code);
      if (params.brand_code) queryParams.append('brand_code', params.brand_code);
      if (params.category_item_code) queryParams.append('category_item_code', params.category_item_code);

      const endpoint = `/products/?${queryParams.toString()}`;
      const response = await apiClient.get<PaginatedResponse<ProductResponse>>(endpoint, headers);
      
      return response;
    } catch (error) {
      console.error('Get products error:', error);
      throw error;
    }
  }

  /**
   * Search products by query string with optional filters
   */
  async searchProducts(query: string, params: { 
    limit?: number; 
    offset?: number; 
    category?: string;
    subcategory?: string;
  } = {}): Promise<ProductSearchResponse> {
    try {
      const headers = getAuthHeaders();
      const queryParams = new URLSearchParams({
        q: query,
      });
      
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.subcategory) queryParams.append('subcategory', params.subcategory);

      const endpoint = `/products/search?${queryParams.toString()}`;
      const response = await apiClient.get<ProductSearchResponse>(endpoint, headers);
      
      return response;
    } catch (error) {
      console.error('Search products error:', error);
      throw error;
    }
  }

  /**
   * Get product by ID
   */
  async getProductById(productId: string): Promise<ProductResponse> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.get<ProductResponse>(`/products/${productId}`, headers);
      return response;
    } catch (error) {
      console.error('Get product by ID error:', error);
      throw error;
    }
  }

  /**
   * Get available filter options with counts (cross-filtered)
   */
  async getProductFilters(params: {
    brand_code?: string;
    category_item_code?: string;
    family_code?: string;
    subfamily_code?: string;
    search?: string;
  } = {}): Promise<{
    brands: Array<{ code: string; name: string; count: number }>;
    category_items: Array<{ code: string; name: string; count: number }>;
    families: Array<{ code: string; name: string; count: number }>;
    subfamilies: Array<{ code: string; name: string; count: number }>;
  }> {
    try {
      const headers = getAuthHeaders();
      const queryParams = new URLSearchParams();
      if (params.brand_code) queryParams.append('brand_code', params.brand_code);
      if (params.category_item_code) queryParams.append('category_item_code', params.category_item_code);
      if (params.family_code) queryParams.append('family_code', params.family_code);
      if (params.subfamily_code) queryParams.append('subfamily_code', params.subfamily_code);
      if (params.search) queryParams.append('search', params.search);

      const endpoint = `/products/filters?${queryParams.toString()}`;
      return await apiClient.get(endpoint, headers);
    } catch (error) {
      console.error('Get product filters error:', error);
      throw error;
    }
  }

  /**
   * Get list of product categories
   */
  async getProductCategories(): Promise<string[]> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.get<string[]>('/products/categories', headers);
      return response;
    } catch (error) {
      console.error('Get product categories error:', error);
      throw error;
    }
  }

  /**
   * Get products by specific category
   */
  async getProductsByCategory(category: string, params: { limit?: number; offset?: number } = {}): Promise<PaginatedResponse<ProductResponse>> {
    try {
      const searchParams: ProductSearchParams = {
        category,
        ...params
      };
      return this.getProducts(searchParams);
    } catch (error) {
      console.error('Get products by category error:', error);
      throw error;
    }
  }

  /**
   * Advanced product filtering
   */
  async filterProducts(filters: {
    categories?: string[];
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<ProductResponse>> {
    try {
      // If multiple categories are provided, we'll need to make multiple requests
      // and combine results (since the API doesn't support multi-category filtering)
      if (filters.categories && filters.categories.length > 1) {
        // For now, just use the first category
        // TODO: Implement proper multi-category filtering when API supports it
        const category = filters.categories[0];
        return this.getProducts({
          category,
          search: filters.search,
          limit: filters.limit,
          offset: filters.offset,
        });
      } else {
        return this.getProducts({
          category: filters.categories?.[0],
          search: filters.search,
          limit: filters.limit,
          offset: filters.offset,
        });
      }
    } catch (error) {
      console.error('Filter products error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const productService = new ProductService();